import { useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, ChevronLeft, ChevronRight, ClipboardPaste, ExternalLink, ImageUp, KeyRound, ScanLine, ShieldCheck, Smartphone } from "lucide-react";

import { ActionButton, Field, Modal, Notice, TextInput, cx } from "../account.ui";
import { firstNameOf, parseAuthenticatorSecret, parseMigrationQr, readQrFromImage } from "./filing.helpers";

import filingService from "../../../../domain/services/filing/filing.service";
import { useFilingCustomerId } from "./filing.scope";

export const SYSTEM_NAMES = { pje: 'PJe', eproc: 'eproc', esaj: 'e-SAJ', projudi: 'Projudi', stj: 'sistema do STJ' };

const STEPS = ['Início', 'No tribunal', 'Enviar QR Code', 'Confirmar'];

const GUIDE_IMAGES = '/images/filing-2fa';

// Passo a passo para gerar um QR novo; telas tiradas dos manuais oficiais (PJe: TJMT; eproc: InfoEproc 7 do TJSP).
const GUIDES = {
    pje: {
        source: 'Telas do PJe do TST e do manual oficial do PJe (TJMT). O login do PDPJ é o mesmo em todos os tribunais do PJe.',
        steps: [
            { title: 'Entre pelo PDPJ', text: 'Abra o PJe de qualquer tribunal (por exemplo, pje.tst.jus.br/tst/login.seam) e clique em "Entrar com PDPJ". Entre com CPF e senha ou com "Seu certificado digital", com o PJeOffice aberto.', image: 'pje-1-tst-entrar-com-pdpj.jpg' },
            { title: 'Peça um novo dispositivo', text: 'Na tela que pede o código do app, clique em "Configurar novo dispositivo". Não clique em Validar.', image: 'pje-2-configurar-novo-dispositivo.jpg' },
            { title: 'Abra o e-mail do CNJ', text: 'Chega o e-mail "Configurar novo aplicativo". Clique no link em até 5 minutos. Se não chegar, veja o spam.', image: 'pje-3-email-link-5min.jpg' },
            { title: 'Tire o print do QR Code', text: 'Aparece o QR Code novo. Tire um print dele e envie na próxima etapa. Depois escaneie o mesmo QR Code no app do celular, digite o código no PJe e clique em Cadastrar.', image: 'pje-4-qr-nao-foi-possivel-ler-cadastrar.jpg' },
        ],
    },
    eproc: {
        source: 'Telas do manual oficial do eproc (InfoEproc 7, TJSP). O caminho é o mesmo no eproc dos outros tribunais (TRF4, TJSC); o TJSC pede o código mesmo no login com certificado.',
        steps: [
            { title: 'Abra o eproc sem entrar', text: 'Preencha usuário e senha, mas não clique em Entrar. Clique em "Autenticação em dois fatores", no canto de baixo. No TJSP e no TJSC, o usuário é a sigla do estado da OAB + o número (ex.: SP123456). Só entra com certificado? Crie a senha em "Esqueci minha senha" (no TJSC, com CPF e data de nascimento; a senha vai para o e-mail cadastrado no eproc) ou, entrando com o certificado, em Usuários > Alterar Senha Pessoal.', image: 'eproc-tjsp-1-login-link-2fa.jpg' },
            { title: 'Desative o 2FA atual', text: 'Clique em "Desativar 2FA". Se você nunca ativou, pule para o passo 4. Tudo o que usa o código atual (celular e outros sistemas) para de funcionar: depois do passo 5, use o novo.', image: 'eproc-tjsp-2-botao-desativar-2fa.jpg' },
            { title: 'Confirme pelo e-mail', text: 'Chega o e-mail "Desativação da Autenticação em 2 Fatores" (no TJSP, de eproc@tjsp.jus.br). Clique no link e depois em Confirmar.', image: 'eproc-tjsp-3-email-desativacao.jpg' },
            { title: 'Ative de novo e tire o print', text: 'Volte ao login, clique em "Autenticação em dois fatores" e em Prosseguir. Aparece o QR Code novo, com a chave em texto embaixo. Tire o print e envie na próxima etapa.', image: 'eproc-tjsp-5-prosseguir-qr-e-chave.jpg' },
            { title: 'Termine no eproc', text: 'Escaneie o mesmo QR Code no app do celular, digite o código e um e-mail pessoal e clique em Enviar. Confirme pelo e-mail em até 60 minutos.', image: 'eproc-tjsp-6-codigo-email-enviar-60min.jpg' },
        ],
    },
    // F389: Projudi do TJPR (passos da DTIC/TJPR). O login com certificado só aceita A3: entramos com CPF + senha + código.
    projudi: {
        source: 'Passos oficiais do TJPR (Acesso ao Projudi com Autenticação em Dois Fatores).',
        steps: [
            { title: 'Entre com CPF e senha', text: 'Abra projudi.tjpr.jus.br/projudi, clique em "Advogados, Partes" e entre com CPF e senha do Projudi (não pelo certificado). Nunca ativou o código? O QR Code já aparece: pule para o passo 4.' },
            { title: 'Peça um novo token', text: 'Na tela que pede o código do autenticador, clique em "Não tem o código autenticador?" e preencha os dados pedidos.' },
            { title: 'Confirme pelo e-mail', text: 'Chega um e-mail de sau@tjpr.jus.br. Clique no link para remover o TOKEN ANTIGO e cadastrar um NOVO.' },
            { title: 'Tire o print do QR Code', text: 'Entre de novo com CPF e senha: aparece o QR Code novo. Tire um print e envie na próxima etapa. Depois escaneie o mesmo QR Code no app do celular e termine a entrada com o código.' },
        ],
    },
};

// TJSC: o QR só sai pelo login com usuário e senha (o tribunal só desativa o 2FA com eles); o certificado não basta.
const LOGIN_REQUIRED_ACCESSES = ['tjsc-eproc'];

function LoginRequiredNotice({ access }) {
    if (access.requiresCourtLogin)
        return (
            <Notice tone="warning" icon={KeyRound} title={`Você vai precisar do CPF e da senha do ${SYSTEM_NAMES[access.system] || 'tribunal'}`}>
                Este tribunal só aceita certificado A3 (token ou cartão) na entrada; o A1 não entra. Por isso protocolamos com CPF, senha e o código do celular, como você. A senha fica guardada cifrada, do mesmo jeito que o certificado.
            </Notice>
        );
    if (!LOGIN_REQUIRED_ACCESSES.includes(access.key))
        return null;
    return (
        <Notice tone="warning" icon={KeyRound} title="Você vai precisar do login e da senha do eproc do TJSC">
            O certificado sozinho não basta: o TJSC só deixa desativar e ativar de novo a autenticação em dois fatores com usuário e senha. Não tem a senha? Na tela de entrada do eproc, clique em "Esqueci minha senha" (pede CPF e data de nascimento; a senha nova vai para o e-mail cadastrado no eproc).
        </Notice>
    );
}

// Também usado na ativação pela Facilita (code.activate.modal), com `check` = como conferir que deu certo.
export function TribunalGuide({ guide, access }) {
    const [index, setIndex] = useState(0);
    const current = guide.steps[index];
    const isLast = index === guide.steps.length - 1;
    const imageUrl = current.image ? `${GUIDE_IMAGES}/${current.image}` : null;

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-start gap-3">
                <div className="min-w-0">
                    <p className="m-0 text-[11px] font-semibold uppercase tracking-wide text-[#8a8a94]">Passo {index + 1} de {guide.steps.length}</p>
                    <p className="m-0 text-sm font-semibold text-[#1d1d1f]">{current.title}</p>
                    <p className="m-0 mt-0.5 text-sm leading-6 text-[#3a3a44]">{current.text}</p>
                </div>
            </div>

            {imageUrl && (
                <a href={imageUrl} target="_blank" rel="noopener noreferrer" title="Abrir a imagem em tamanho real" className="flex justify-center rounded-xl border border-[#ececf0] bg-[#fafafb] p-3">
                    <img src={imageUrl} alt={current.title} className="max-h-72 w-auto max-w-full rounded-md object-contain" />
                </a>
            )}

            {current.check && <Notice tone="success" icon={CheckCircle2} title="Como conferir">{current.check}</Notice>}

            <div className="flex items-center justify-between gap-3">
                <ActionButton variant="ghost" size="sm" icon={ChevronLeft} disabled={index === 0} onClick={() => setIndex(index - 1)}>Anterior</ActionButton>
                <span className="flex gap-1.5" aria-label={`Passo ${index + 1} de ${guide.steps.length}`}>
                    {guide.steps.map((item, position) => (
                        <button
                            key={item.title}
                            type="button"
                            aria-label={`Ir para o passo ${position + 1}`}
                            onClick={() => setIndex(position)}
                            className={cx('h-2 rounded-full transition-all', position === index ? 'w-5 bg-primary' : 'w-2 bg-[#dcdce2]')}
                        />
                    ))}
                </span>
                <ActionButton variant="ghost" size="sm" disabled={isLast} onClick={() => setIndex(index + 1)}>Próximo <ChevronRight className="h-4 w-4" aria-hidden="true" /></ActionButton>
            </div>

            <div className="flex flex-col gap-1">
                {access.setupUrl && (
                    <a href={access.setupUrl} target="_blank" rel="noopener noreferrer" className="inline-flex w-fit items-center gap-1.5 text-sm font-medium text-primary hover:underline">
                        Abrir o {SYSTEM_NAMES[access.system] || 'sistema do tribunal'} em outra aba <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                    </a>
                )}
                <p className="m-0 text-[11px] text-[#9a9aa3]">{guide.source}</p>
            </div>
        </div>
    );
}

function Stepper({ current }) {
    return (
        <ol className="m-0 mb-5 flex list-none items-center gap-2 p-0" aria-label="Etapas">
            {STEPS.map((label, index) => (
                <li key={label} className="flex min-w-0 flex-1 items-center gap-2">
                    <span
                        aria-current={index === current ? 'step' : undefined}
                        className={cx(
                            'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                            index < current && 'bg-primary text-white',
                            index === current && 'bg-primaryTint text-primary ring-2 ring-primary',
                            index > current && 'bg-[#f2f2f5] text-[#8a8a94]',
                        )}
                    >
                        {index < current ? <CheckCircle2 className="h-4 w-4" aria-hidden="true" /> : index + 1}
                    </span>
                    <span className={cx('truncate text-xs', index === current ? 'font-medium text-[#1d1d1f]' : 'text-[#8a8a94]')}>{label}</span>
                    {index < STEPS.length - 1 && <span className="h-px flex-1 bg-[#e3e3e8]" aria-hidden="true" />}
                </li>
            ))}
        </ol>
    );
}

// Um só campo de verdade (cola, autocompletar do SMS e leitor de tela funcionam) desenhado como 6 caixas.
function CodeInput({ value, onChange, onSubmit, invalid, disabled }) {
    const [isFocused, setIsFocused] = useState(false);
    const digits = value.split('');

    return (
        <label className="relative block w-fit">
            <span className="sr-only">Código de 6 dígitos</span>
            <input
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                disabled={disabled}
                value={value}
                aria-invalid={invalid || undefined}
                onChange={(event) => onChange(event.target.value.replace(/\D/g, '').slice(0, 6))}
                onKeyDown={(event) => { if (event.key === 'Enter' && value.length === 6) onSubmit?.(); }}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                className="absolute inset-0 h-full w-full cursor-text opacity-0"
                autoFocus
            />
            <span className="flex gap-2" aria-hidden="true">
                {[0, 1, 2, 3, 4, 5].map(index => {
                    const isActive = isFocused && (index === digits.length || (index === 5 && digits.length === 6));

                    return (
                        <span
                            key={index}
                            className={cx(
                                'flex h-12 w-11 items-center justify-center rounded-lg border bg-white text-lg font-semibold tabular-nums text-[#1d1d1f] transition',
                                invalid ? 'border-[#c1341a]' : isActive ? 'border-primary ring-4 ring-[color-mix(in_srgb,var(--color-primary)_14%,transparent)]' : 'border-[#dcdce2]',
                                disabled && 'opacity-60',
                            )}
                        >
                            {digits[index] || ''}
                        </span>
                    );
                })}
            </span>
        </label>
    );
}

export default function AccessConnectModal({ open, access, certificate, customerId: requestedCustomerId, onClose, onConnected }) {
    // F168: a equipe anexa o print do QR que o cliente mandou e conecta direto, sem o código do app.
    const scopeCustomerId = useFilingCustomerId();
    const customerId = requestedCustomerId ?? scopeCustomerId;
    const isTeam = !!customerId;
    const firstStep = isTeam ? 2 : 0;
    const fileRef = useRef(null);
    const [step, setStep] = useState(firstStep);
    const [rawKey, setRawKey] = useState('');
    const [parsed, setParsed] = useState(null);
    const [qrError, setQrError] = useState(null);
    const [isReading, setIsReading] = useState(false);
    const [code, setCode] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [done, setDone] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [showKeyField, setShowKeyField] = useState(false);
    const [password, setPassword] = useState('');

    useEffect(() => {
        if (!open)
            return;

        setStep(firstStep);
        setRawKey('');
        setParsed(null);
        setQrError(null);
        setCode('');
        setPassword('');
        setError(null);
        setDone(false);
    }, [open, firstStep]);

    if (!access || !certificate)
        return null;

    const isReconnect = certificate.access?.[access.key]?.status === 'reconnect';
    const holderFirstName = firstNameOf({ certificate });
    const guide = GUIDES[access.system] || GUIDES.pje;
    // F389: login por CPF + senha (Projudi TJPR). Ao reconectar, deixar em branco mantém a senha guardada.
    const asksPassword = !!access.requiresCourtLogin;
    const missingPassword = asksPassword && !isReconnect && !password;
    const systemName = SYSTEM_NAMES[access.system] || 'tribunal';

    const applyKeyText = (text) => {
        setRawKey(text);
        setQrError(null);
        setParsed(parseAuthenticatorSecret(text));
    };

    const readImage = async (file) => {
        if (!file)
            return;

        setIsReading(true);
        setQrError(null);
        try {
            const data = await readQrFromImage(file);
            const result = parseAuthenticatorSecret(data);
            if (!result)
                throw new Error(parseMigrationQr(data)?.multiple ? 'QR_MANY_ACCOUNTS' : 'QR_NOT_AUTHENTICATOR');

            setRawKey('');
            setParsed({ ...result, fromImage: file.name });
        } catch (failure) {
            setParsed(null);
            setQrError(failure.message === 'QR_MANY_ACCOUNTS'
                ? 'Esse QR Code exporta várias contas do autenticador. No app, exporte só a conta do tribunal e envie o QR Code de novo.'
                : failure.message === 'QR_NOT_AUTHENTICATOR'
                ? 'Esse QR Code não é de autenticador. Envie o QR Code da tela de dois fatores do tribunal.'
                : 'Não achamos um QR Code nessa imagem. Tire um print só da área do QR Code, sem cortar as bordas.');
        } finally {
            setIsReading(false);
        }
    };

    const pasteImage = (event) => {
        const image = Array.from(event.clipboardData?.files || []).find(item => item.type.startsWith('image/'));
        if (image) {
            event.preventDefault();
            readImage(image);
        }
    };

    const pasteFromClipboard = async () => {
        try {
            const items = await navigator.clipboard.read();
            for (const item of items) {
                const imageType = item.types.find(type => type.startsWith('image/'));
                if (imageType) {
                    const blob = await item.getType(imageType);
                    return readImage(new File([blob], 'print.png', { type: imageType }));
                }
            }

            const text = await navigator.clipboard.readText();
            applyKeyText(text);
        } catch (denied) {
            setQrError('O navegador não deixou colar. Clique na área do print e use Ctrl+V, ou escolha o arquivo.');
        }
    };

    const confirm = async () => {
        if (!isTeam && code.length !== 6)
            return setError('Digite os 6 números que aparecem no app agora.');
        if (missingPassword)
            return setError(`Informe a senha do ${systemName}.`);

        setIsSaving(true);
        setError(null);
        try {
            const updated = await filingService.connectAccess({ certificateId: certificate.id, accessKey: access.key, secret: parsed.secret, code: isTeam ? undefined : code, customerId, ...(asksPassword && password ? { password } : {}) });
            setDone(true);
            onConnected?.(updated);
        } catch (failure) {
            setError(filingService.errorMessageOf(failure));
            setCode('');
        } finally {
            setIsSaving(false);
        }
    };

    if (done)
        return (
            <Modal
                open={open}
                onClose={onClose}
                size="sm"
                title={<span className="sr-only">{access.name} conectado</span>}
                footer={<ActionButton onClick={onClose}>Concluir</ActionButton>}
            >
                <div className="flex flex-col items-center gap-3 pb-2 text-center">
                    <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#e8f6ee] text-[#1f7a4f]">
                        <ShieldCheck className="h-8 w-8" aria-hidden="true" />
                    </span>
                    <p className="m-0 text-lg font-semibold text-[#1d1d1f]">{access.name} conectado</p>
                    <p className="m-0 max-w-sm text-sm leading-6 text-[#6b6b76]">
                        Pronto para usar. Protocolamos e distribuímos com o certificado de {holderFirstName}, sem precisar do celular. Você continua entrando no tribunal pelo seu aplicativo, como sempre.
                    </p>
                </div>
            </Modal>
        );

    const footerByStep = [
        (
            <>
                <ActionButton variant="secondary" onClick={onClose}>Cancelar</ActionButton>
                <ActionButton onClick={() => setStep(1)}>Começar</ActionButton>
            </>
        ),
        (
            <>
                <ActionButton variant="secondary" onClick={() => setStep(0)}>Voltar</ActionButton>
                <ActionButton onClick={() => setStep(2)}>Estou com o QR Code na tela</ActionButton>
            </>
        ),
        (
            <>
                {isTeam ? (
                    <>
                        <ActionButton variant="secondary" onClick={onClose} disabled={isSaving}>Cancelar</ActionButton>
                        <ActionButton onClick={confirm} loading={isSaving} disabled={!parsed || missingPassword}>Conectar</ActionButton>
                    </>
                ) : (
                    <>
                        <ActionButton variant="secondary" onClick={() => setStep(1)}>Voltar</ActionButton>
                        <ActionButton onClick={() => setStep(3)} disabled={!parsed}>Continuar</ActionButton>
                    </>
                )}
            </>
        ),
        (
            <>
                <ActionButton variant="secondary" onClick={() => setStep(2)} disabled={isSaving}>Voltar</ActionButton>
                <ActionButton onClick={confirm} loading={isSaving} disabled={code.length !== 6 || missingPassword}>Conferir e conectar</ActionButton>
            </>
        ),
    ];

    return (
        <Modal
            open={open}
            onClose={onClose}
            dismissible={!isSaving}
            icon={KeyRound}
            size={step === 1 ? 'lg' : 'md'}
            title={`${isReconnect ? 'Reconectar' : 'Conectar'} ${access.name}`}
            description={isTeam ? `Acesso de ${holderFirstName}. Anexe o print do QR Code que o cliente mandou.` : `Acesso de ${holderFirstName}. Leva cerca de 5 minutos.`}
            footer={footerByStep[step]}
        >
            {!isTeam && <Stepper current={step} />}

            {step === 0 && (
                <div className="flex flex-col gap-4">
                    <LoginRequiredNotice access={access} />
                    <p className="m-0 text-sm leading-6 text-[#3a3a44]">
                        O tribunal pede um código do celular a cada entrada. Para protocolarmos por você, vamos guardar a chave desse código, do mesmo jeito que o seu app autenticador faz.
                    </p>
                    <ul className="m-0 flex list-none flex-col gap-3 p-0">
                        <li className="flex gap-3 text-sm"><Smartphone className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" /><span>Deixe à mão o celular com o app autenticador (Google Authenticator, Microsoft Authenticator ou outro).</span></li>
                        <li className="flex gap-3 text-sm"><ScanLine className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" /><span>Você vai gerar um QR Code novo no tribunal, mandar o print para nós e escanear o mesmo QR Code no seu app.</span></li>
                    </ul>
                    <Notice tone="brand" icon={Smartphone} title="Você continua entrando no tribunal pelo seu aplicativo">
                        O app do seu celular segue gerando o código, como hoje. Nós só guardamos uma cópia da chave para protocolar sem pedir o código a você.
                    </Notice>
                    <Notice tone="warning" icon={AlertTriangle} title="Antes de começar: avise quem usa o mesmo acesso">
                        O QR Code novo substitui o antigo. Se outras pessoas do escritório entram no tribunal com este CPF, o código antigo deixa de funcionar para elas: cada uma precisa escanear o QR Code novo no próprio app.
                    </Notice>
                </div>
            )}

            {step === 1 && <TribunalGuide guide={guide} access={access} />}

            {step === 2 && (
                <div className="flex flex-col gap-5">
                    {isTeam && <LoginRequiredNotice access={access} />}
                    <div className="flex flex-col gap-3">
                        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(event) => { readImage(event.target.files?.[0]); event.target.value = ''; }} />
                        <button
                            type="button"
                            onClick={() => fileRef.current?.click()}
                            onPaste={(event) => pasteImage(event)}
                            onDragOver={(event) => { event.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={(event) => { event.preventDefault(); setIsDragging(false); readImage(event.dataTransfer.files?.[0]); }}
                            className={cx(
                                'flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed px-4 py-6 text-center transition',
                                parsed?.fromImage ? 'border-[#9fd8b8] bg-[#f1faf5]' : isDragging ? 'border-primary bg-primaryTint' : 'border-[#dcdce2] bg-[#fafafb] hover:border-primary',
                            )}
                        >
                            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primaryTint text-primary">
                                {isReading ? <ScanLine className="h-5 w-5 animate-pulse" aria-hidden="true" /> : <ImageUp className="h-5 w-5" aria-hidden="true" />}
                            </span>
                            <span className="text-sm font-semibold text-[#1d1d1f]">{parsed?.fromImage ? 'Print recebido' : 'Envie o print do QR Code'}</span>
                            {parsed?.fromImage && <span className="text-xs font-medium text-primary">Trocar imagem</span>}
                            <span className="text-xs text-[#6b6b76]">Clique para escolher, arraste a imagem ou cole com Ctrl+V. A imagem é lida aqui no seu navegador.</span>
                        </button>
                        {!!navigator.clipboard?.read && (
                            <ActionButton variant="ghost" size="sm" icon={ClipboardPaste} onClick={pasteFromClipboard} className="self-center">Colar print copiado</ActionButton>
                        )}

                        {showKeyField ? (
                            <Field label="Chave em texto" htmlFor="authenticator-key" hint="Aparece junto do QR Code, em letras e números.">
                                <TextInput
                                    id="authenticator-key"
                                    autoComplete="off"
                                    spellCheck={false}
                                    placeholder="Ex.: JBSW Y3DP EHPK 3PXP"
                                    value={rawKey}
                                    invalid={!!rawKey && !parsed}
                                    onChange={applyKeyText}
                                    onPaste={pasteImage}
                                    className="font-mono tracking-wide"
                                />
                            </Field>
                        ) : (
                            <button type="button" onClick={() => setShowKeyField(true)} className="self-center text-xs font-medium text-[#6b6b76] underline-offset-2 hover:text-primary hover:underline">
                                Não consegue o print? Digite a chave em texto
                            </button>
                        )}
                        {qrError && <p className="m-0 text-xs text-[#c1341a]" role="alert">{qrError}</p>}
                        {isTeam && asksPassword && (
                            <Field
                                label={`Senha do ${systemName}`}
                                htmlFor="court-password"
                                hint={isReconnect ? 'Deixe em branco para manter a senha já guardada.' : `A que ${holderFirstName} usa para entrar com o CPF. Fica guardada cifrada.`}
                            >
                                <TextInput id="court-password" type="password" autoComplete="off" value={password} disabled={isSaving} onChange={(value) => { setPassword(value); setError(null); }} />
                            </Field>
                        )}
                        {isTeam && error && <Notice tone="danger">{error}</Notice>}
                        {!!rawKey && !parsed && !qrError && <p className="m-0 text-xs text-[#c1341a]">Essa chave não parece completa. Confira se copiou tudo.</p>}

                        {parsed && (
                            <div className="flex items-center gap-2 rounded-lg bg-[#f1faf5] px-3 py-2 text-[13px] text-[#17553a]">
                                <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
                                <span className="min-w-0 truncate">{parsed.fromImage ? 'QR Code lido.' : 'Chave reconhecida.'}{parsed.account ? ` Conta ${parsed.account.replace(/^[^:]*:/, '')}` : ''}</span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {step === 3 && (
                <div className="flex flex-col gap-4">
                    <p className="m-0 text-sm leading-6 text-[#3a3a44]">
                        Escaneie <strong>o mesmo QR Code</strong> no app autenticador do seu celular e digite o código que aparece agora. Assim conferimos que a chave está certa antes de salvar.
                    </p>
                    <CodeInput value={code} onChange={(value) => { setCode(value); setError(null); }} onSubmit={confirm} invalid={!!error} disabled={isSaving} />
                    {asksPassword && (
                        <Field
                            label={`Senha do ${systemName}`}
                            htmlFor="court-password"
                            hint={isReconnect ? 'Deixe em branco para manter a senha já guardada.' : `A que ${holderFirstName} usa para entrar com o CPF. Fica guardada cifrada.`}
                        >
                            <TextInput id="court-password" type="password" autoComplete="off" value={password} disabled={isSaving} onChange={(value) => { setPassword(value); setError(null); }} />
                        </Field>
                    )}
                    {error && <Notice tone="danger">{error}</Notice>}
                    <p className="m-0 text-xs text-[#8a8a94]">O código muda a cada 30 segundos. Se trocar enquanto digita, use o novo.</p>
                </div>
            )}
        </Modal>
    );
}
